/**
 * shadcn/ui Button, restyled to the Alpine tokens.
 *
 * Only the variants this product actually uses are kept. Radix' `Slot`/`asChild`
 * is not pulled in: nothing here needs to render as another element, and the
 * brief asks for a lean bundle rather than a full component library import.
 */
import { cva, type VariantProps } from 'class-variance-authority'
import type { ButtonHTMLAttributes } from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium',
    'rounded-sm border transition-[background-color,color,border-color,opacity,transform]',
    'duration-fast ease-enter select-none',
    'disabled:pointer-events-none disabled:opacity-60',
    // Motion budget: no travel at all on a button, only colour.
    'active:translate-y-px',
    '[&_svg]:shrink-0 [&_svg]:size-4',
  ),
  {
    variants: {
      variant: {
        primary: 'bg-accent text-white border-accent hover:bg-accent-hover hover:border-accent-hover',
        secondary: 'bg-white text-ink border-border-strong hover:bg-surface',
        ghost: 'bg-transparent text-ink-2 border-transparent hover:bg-surface',
      },
      size: {
        sm: 'h-8 px-3 text-[13px]',
        md: 'h-10 px-4 text-[15px]',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export function Button({ className, variant, size, type = 'button', ...props }: ButtonProps) {
  return <button type={type} className={cn(buttonVariants({ variant, size }), className)} {...props} />
}

export { buttonVariants }
